import { clamp } from 'lodash-es';

import { type Theme, alpha as toRgba } from '@mui/material/styles';

import { ColorCode, type ColorString } from '../types/colors';

import {
  DefaultSecondaryColorNames,
  type EvaluatorColorOptions,
  InvalidColorError,
  isMuiPalettelessColor,
  type MuiPaletteColorName,
  parseMuiPaletteColorName,
} from './mui';

type ParsedRgb = {
  readonly b: number;
  readonly g: number;
  readonly r: number;
};

type ParseRgba = {
  readonly a: number;
} & ParsedRgb;

const toRgbaString = (parsed: ParseRgba): ColorString<typeof ColorCode.Rgba> =>
  `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${parsed.a})`;

const toRgbString = (parsed: ParsedRgb): ColorString<typeof ColorCode.Rgb> =>
  `rgb(${parsed.r}, ${parsed.g}, ${parsed.b})`;

const RgbRegex = /rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/;
const RgbaRegex = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/;
const HexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

const ColorCodeRegexes: Record<ColorCode, RegExp> = {
  [ColorCode.Hex]: HexRegex,
  [ColorCode.Rgb]: RgbRegex,
  [ColorCode.Rgba]: RgbaRegex,
};

const applyColorCodeRegex = (color: string, format: ColorCode) => {
  const regex = ColorCodeRegexes[format];
  return regex.exec(color);
};

export const isColorOfCode = <C extends ColorCode>(
  color: string,
  code: C,
): color is ColorString<C> => {
  const regex = ColorCodeRegexes[code];
  return regex.test(color);
};

export const isColorOfAnyCode = (color: string): color is ColorString =>
  Object.values(ColorCode).some(code => isColorOfCode(color, code));

type ColorCodeAssertion = <C extends ColorCode>(
  color: string,
  code: C,
) => asserts color is ColorString<C>;

export const assertIsColorOfCode: ColorCodeAssertion = <C extends ColorCode>(
  color: string,
  code: C,
): asserts color is ColorString<C> => {
  if (!isColorOfCode<C>(color, code)) {
    throw new Error(`The color '${color}' is not a valid ${code.toUpperCase()} color!`);
  }
};

const parseColorCode = (color: string): ColorCode | null => {
  for (const code of Object.values(ColorCode)) {
    if (isColorOfCode(color, code)) {
      return code;
    }
  }
  return null;
};

const parseRgbColor = (color: string): ParsedRgb => {
  const parts = applyColorCodeRegex(color, ColorCode.Rgb);
  if (!parts) {
    throw new Error(`The value '${color}' is not a valid RGB color string!`);
  }
  return {
    b: parseInt(parts[3], 10),
    g: parseInt(parts[2], 10),
    r: parseInt(parts[1], 10),
  };
};

const parseRgbaColor = (color: string): ParseRgba => {
  const parts = applyColorCodeRegex(color, ColorCode.Rgba);
  if (!parts) {
    throw new Error(`The value '${color}' is not a valid RGBA color string!`);
  }
  const alphaPart = parts[4] as (typeof parts)[4] | undefined;
  return {
    a: alphaPart ? parseFloat(alphaPart) : 1,
    b: parseInt(parts[3], 10),
    g: parseInt(parts[2], 10),
    r: parseInt(parts[1], 10),
  };
};

export type StandardizeColorOptions =
  | {
      readonly alpha?: never;
      readonly format: typeof ColorCode.Hex | typeof ColorCode.Rgb;
      readonly strict?: boolean;
    }
  | {
      readonly alpha?: number;
      readonly format: typeof ColorCode.Rgba;
      readonly strict?: boolean;
    };

const rgbToHex = (parsed: ParsedRgb): ColorString<typeof ColorCode.Hex> => {
  const toHex = (code: number) => {
    const hex = clamp(code, 0, 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(parsed.r)}${toHex(parsed.g)}${toHex(parsed.b)}`;
};

type StandardizedColor<O extends StandardizeColorOptions> = O extends {
  readonly format: infer F extends ColorCode;
  readonly strict: false;
}
  ? ColorString<F> | null
  : O extends {
        readonly format: infer F extends ColorCode;
      }
    ? ColorString<F>
    : never;

const ColorStandards: Record<
  ColorCode,
  {
    [ColorCode.Hex]: (color: string) => ColorString<typeof ColorCode.Hex>;
    [ColorCode.Rgb]: (color: string) => ColorString<typeof ColorCode.Rgb>;
    [ColorCode.Rgba]: (
      color: string,
      alpha: number | undefined,
    ) => ColorString<typeof ColorCode.Rgba>;
  }
> = {
  [ColorCode.Hex]: {
    [ColorCode.Hex]: color => {
      assertIsColorOfCode(color, ColorCode.Hex);
      return color;
    },
    [ColorCode.Rgb]: color => {
      const parsed = parseRgbaColor(toRgba(color, 1));
      return toRgbString(parsed);
    },
    [ColorCode.Rgba]: (c, alpha) => {
      const parsed = parseRgbaColor(toRgba(c, alpha ?? 1));
      return toRgbaString({ ...parsed, a: alpha ?? parsed.a });
    },
  },
  [ColorCode.Rgb]: {
    [ColorCode.Hex]: color => rgbToHex(parseRgbColor(color)),
    [ColorCode.Rgb]: color => {
      assertIsColorOfCode(color, ColorCode.Rgb);
      return color;
    },
    [ColorCode.Rgba]: (c, alpha) => {
      const parsed = parseRgbColor(c);
      return toRgbaString({ ...parsed, a: alpha ?? 1 });
    },
  },
  [ColorCode.Rgba]: {
    [ColorCode.Hex]: color => {
      const parsed = parseRgbaColor(color);
      return rgbToHex(parsed);
    },
    [ColorCode.Rgb]: color => {
      const parsed = parseRgbaColor(color);
      return toRgbString(parsed);
    },
    [ColorCode.Rgba]: (c, alpha) => {
      const parsed = parseRgbaColor(c);
      return toRgbaString({ ...parsed, a: alpha ?? parsed.a });
    },
  },
};

/**
 * Standardizes the provided color string into the specified format, {@link ColorCode}.
 *
 * @param {string} color
 *   The color string to standardize. This can be in any of the supported formats: HEX, RGB, or
 *   RGBA.
 * @param {StandardizeColorOptions} options
 *   Options that define how the color is standardized.
 *
 *   @see {@link StandardizeColorOptions}
 *
 * @example
 * ```ts
 * standardizeColor('#FF5733', { format: ColorCode.Rgb });
 * // Returns: 'rgb(255, 87, 51)'
 * ```
 *
 * @returns {StandardizedColor<O>}
 *   The standardized color string in the specified format, or `null` if the provided color
 *   string could not be parsed and the `strict` option is set to `false`.
 */
export const standardizeColor = <O extends StandardizeColorOptions>(
  color: string,
  { alpha: _alpha, format, strict = true }: O,
): StandardizedColor<O> => {
  const alpha = _alpha === undefined ? 1 : clamp(_alpha, 0, 1);

  const currentFormat = parseColorCode(color);
  if (!currentFormat) {
    if (strict) {
      throw new Error(`Could not determine the format of the color string: ${color}!`);
    }
    return null as StandardizedColor<O>;
  }
  const standard = ColorStandards[currentFormat][format];
  return standard(color, format === ColorCode.Rgba ? alpha : undefined) as StandardizedColor<O>;
};

export type Color = ColorString | MuiPaletteColorName;

export function evaluateHtmlColor(theme: Theme, color: 'inherit' | Color): string;

export function evaluateHtmlColor<O extends EvaluatorColorOptions>(
  theme: Theme,
  color: ((t: Theme) => string) | string,
  options?: O,
): string;

/**
 * Evaluates the HTML compatible color value for the provided color,
 * {@link MuiThemeCallbackOrValue<string>}, which may be defined as a {@link ColorString} (i.e.
 * a HEX, RGBA or RGB color) or a MUI specific color string that exists on the {@link Palette},
 * specified as - for instance, 'primary.main'.
 *
 * @param {MuiThemeCallbackOrValue<string>} color
 *   The color that the HTML-compatible color string should be returned for.
 *
 * @returns {string} The HTML-compatible color value.
 */
export function evaluateHtmlColor<O extends EvaluatorColorOptions>(
  theme: Theme,
  color: ((t: Theme) => string) | string,
  options?: O,
): string {
  if (typeof color === 'function') {
    const evaluated = color(theme);
    return evaluateHtmlColor(theme, evaluated, options);
  } else if (color === 'inherit') {
    return color;
  } else if (isColorOfAnyCode(color)) {
    return color;
  } else if (isMuiPalettelessColor(color)) {
    return theme.palette[color];
  }
  const parsed = parseMuiPaletteColorName(theme, color, { strict: false });
  if (!parsed) {
    if (options?.strict === false) {
      return color;
    }
    throw new InvalidColorError(color);
  }
  const [primary, secondary] = parsed;

  const subPalette = theme.palette[primary];
  const key = secondary ?? DefaultSecondaryColorNames[primary];
  return subPalette[key as keyof typeof subPalette];
}
